import React from "react";
import Typography from "@insite/mobius/Typography";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button from "@insite/mobius/Button";
import { css } from "styled-components";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";

const enum fields {
    image = "image",
    imageSize = "imageSize",
    backgroundColor = "backgroundColor",
    altText = "altText",
    heading = "heading",
    subheading = "subheading",
    paddingTop = 'paddingTop',
    paddingBottom = 'paddingBottom',
    paddingLeft = 'paddingLeft',
    paddingRight = 'paddingRight',
    imageWidth = "imageWidth"
}

interface Props extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageSize]: string;
        [fields.backgroundColor]: string;
        [fields.altText]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.paddingTop]: string;
        [fields.paddingBottom]: string;
        [fields.paddingLeft]: string;
        [fields.paddingRight]: string;
        [fields.imageWidth]: string;

    }
}

const CustomFullBleedBanner: React.FC<Props> = (props) => {

    //the typography component wraps things in a span so I'm targeting the p and h3 etc... to remove margins.
    const bannerWrapperStyles = {
        css: css`
                background-color: ${props.fields.backgroundColor};
                width:100%;
                display:flex;
                justify-content: center;
                align-items: center;
                padding-top: ${props.fields.paddingTop};
                padding-bottom: ${props.fields.paddingBottom};
                padding-left: ${props.fields.paddingLeft};
                padding-right: ${props.fields.paddingRight};
                p, h4, h3, h2 {
                    margin: 0;
                }
            `,
    };

    const imageWrapperStyles = {
        css: css`
            height: 100%;
            margin: 0 1rem;
        `,
    }

    const imageStyles = {
        css: css`
        height: auto;
        width: ${props.fields.imageWidth};
        `,
    }
    const leftSideTextStyles = {
        css: css`
            margin: 0 2rem 0 0;
        `,
    }
    const rightSideTextStyles = {
        css: css`
            flex-basis:24%;
            margin-bottom: 0;
            p {
                font-size: 12px;
                font-weight: 300;
            }
        
        `
    }

    return (
        <StyledWrapper {...bannerWrapperStyles}>
            <Typography {...leftSideTextStyles} >{parse(props.fields.heading, parserOptions)}</Typography>
            <StyledWrapper {...imageWrapperStyles}>
                <LazyImage {...imageStyles} src={props.fields.image} altText={props.fields.altText} />
            </StyledWrapper>
            <Typography {...rightSideTextStyles}>{parse(props.fields.subheading, parserOptions)}</Typography>
        </StyledWrapper>
    )
}

const widgetModule: WidgetModule = {
    component: CustomFullBleedBanner,
    definition: {
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Center Image Picker",
                tooltip: "Image Size has to be 330 x 229",
                defaultValue: "https://i.imgur.com/WV1G2Un.png%22%20title=%22source:%20imgur.com",
            },
            {
                fieldType: "Translatable",
                name: fields.imageSize,
                editorTemplate: "TextField",
                displayName: "Center Recommended Image Size",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
            },
            {
                fieldType: "Translatable",
                name: fields.imageWidth,
                editorTemplate: "TextField",
                displayName: "Image Width Top (px/%/rem)",
                defaultValue: "110",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Left side text",
                defaultValue: "",
                extendedConfig: { height: 100 },
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "right side text",
                defaultValue: "",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "Translatable",
                name: fields.paddingTop,
                editorTemplate: "TextField",
                displayName: "Padding Top (px/%/rem)",
                defaultValue: "0",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingBottom,
                editorTemplate: "TextField",
                displayName: "Padding Bottom (px/%/rem)",
                defaultValue: "0",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingLeft,
                editorTemplate: "TextField",
                displayName: "Padding Left (px/%/rem)",
                defaultValue: "0",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingRight,
                editorTemplate: "TextField",
                displayName: "Padding Right (px/%/rem)",
                defaultValue: "0",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;